import type { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';

import {
  createAccountFromTokenData,
  createAuthenticationResult,
} from '@/helper/auth/oauth-account';
import {
  buildRefreshTokenRequestParams,
  buildTokenRequestParams,
} from '@/helper/auth/oauth-url';

/** Authorization-code + refresh-token exchange result (native). */
export type CodeExchangeResult = AuthenticationResult & {
  refreshToken?: string;
};

/** Bridge method injected on native; avoids CapacitorHttp proxy falling back to fetch. */
type CapacitorWithNativeBridge = typeof Capacitor & {
  nativePromise?: <R>(
    pluginId: string,
    methodName: string,
    options: Record<string, unknown>,
  ) => Promise<R>;
};

async function postTokenForm(
  tokenEndpoint: string,
  params: URLSearchParams,
): Promise<unknown> {
  if (Capacitor.isNativePlatform()) {
    const cap = Capacitor as CapacitorWithNativeBridge;
    if (typeof cap.nativePromise !== 'function') {
      throw new Error('Capacitor native bridge is not available for token exchange');
    }
    const response = (await cap.nativePromise('CapacitorHttp', 'request', {
      url: tokenEndpoint,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: params.toString(),
      responseType: 'json',
    })) as {
      status: number;
      data: unknown;
    };
    if (response.status < 200 || response.status >= 300) {
      const errorText =
        typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data ?? {});
      throw new Error(`Token exchange failed: ${errorText}`);
    }
    return response.data;
  }

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed: ${errorText}`);
  }
  return response.json();
}

function toCodeExchangeResult(
  tokenData: Record<string, unknown>,
  authority: string,
): CodeExchangeResult {
  const account = createAccountFromTokenData(tokenData, authority);
  const authenticationResult = createAuthenticationResult(
    tokenData,
    account,
    authority,
  );
  const refreshToken =
    typeof tokenData.refresh_token === 'string' ? tokenData.refresh_token : undefined;
  return {
    ...authenticationResult,
    refreshToken,
  };
}

async function persistMsalCacheFromExchange(
  msalInstance: PublicClientApplication,
  result: CodeExchangeResult,
  authority: string,
): Promise<void> {
  const { refreshToken: _drop, ...authResult } = result;
  await msalInstance.hydrateCache(authResult, {
    scopes: authResult.scopes,
    authority,
    account: authResult.account!,
  });
  if (result.account) {
    await msalInstance.setActiveAccount(result.account);
  }
}

/**
 * Exchange authorization code for tokens (native strategy only — manual POST so redirect_uri is sent).
 */
export async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  msalInstance: PublicClientApplication,
  getRedirectUri: () => string,
  msalConfig: { auth: { authority: string; clientId: string } },
): Promise<CodeExchangeResult> {
  const authority = msalConfig.auth.authority;
  const clientId = msalConfig.auth.clientId;
  const tokenEndpoint = `${authority}/oauth2/v2.0/token`;
  const params = buildTokenRequestParams(
    clientId,
    code,
    getRedirectUri(),
    codeVerifier,
  );

  const tokenData = (await postTokenForm(tokenEndpoint, params)) as Record<
    string,
    unknown
  >;
  const result = toCodeExchangeResult(tokenData, authority);
  await persistMsalCacheFromExchange(msalInstance, result, authority);
  const storedAccounts = msalInstance.getAllAccounts();
  console.log(
    'After setActiveAccount, getAllAccounts returned:',
    storedAccounts.length,
    'accounts',
  );
  return result;
}

export type RefreshWithRotationOptions = {
  /** If Entra omits id_token on refresh, use this snapshot (e.g. from localStorage). */
  fallbackIdToken?: string;
};

/**
 * New access token (+ optional rotated refresh) via refresh_token grant (native).
 */
export async function refreshTokensWithRefreshToken(
  refreshToken: string,
  msalInstance: PublicClientApplication,
  msalConfig: { auth: { authority: string; clientId: string } },
  options?: RefreshWithRotationOptions,
): Promise<CodeExchangeResult> {
  const authority = msalConfig.auth.authority;
  const clientId = msalConfig.auth.clientId;
  const tokenEndpoint = `${authority}/oauth2/v2.0/token`;
  const params = buildRefreshTokenRequestParams(clientId, refreshToken);

  const raw = (await postTokenForm(tokenEndpoint, params)) as Record<
    string,
    unknown
  >;

  if (!raw.id_token && options?.fallbackIdToken) {
    raw.id_token = options.fallbackIdToken;
  }
  if (!raw.id_token) {
    throw new Error('Token refresh failed: no id_token in response');
  }

  const result = toCodeExchangeResult(raw, authority);
  await persistMsalCacheFromExchange(msalInstance, result, authority);
  return result;
}
